import { REMINDER_HORIZON_DAYS, todayIso, type ReminderKind } from "@szerviz/core";
import { useCallback, useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { ErrorText } from "../components/ErrorText";
import { Help } from "../components/Help";
import { Plate } from "../components/Plate";
import { useDay } from "../components/Reminders";
import { useApp, useFeature } from "../context";
import type { SentReminder, UpcomingReminder } from "../data";

/** Why "Send now" is grey for a row (null: it can go now). */
function blocked(r: UpcomingReminder): string | null {
  if (!r.customerId) return "reminders.why.noCustomer";
  if (!r.consent) return "reminders.why.noConsent";
  if (r.channels.length === 0) return r.sentChannels.length > 0 ? "reminders.why.allSent" : "reminders.why.noContact";
  return null;
}

/**
 * Emlékeztetők: what falls due in the next 60 days and what was sent. Phone first: one card per
 * reminder with a big "Send now" button; the reason is written next to it when it cannot go.
 */
export function Reminders() {
  const { t, i18n } = useTranslation();
  const { store, workshop, go } = useApp();
  const on = useFeature("reminders");
  const day = useDay();
  const [upcoming, setUpcoming] = useState<UpcomingReminder[] | null>(null);
  const [sent, setSent] = useState<SentReminder[]>([]);
  const [error, setError] = useState<unknown>(null);
  const [info, setInfo] = useState<string | null>(null);
  const [busy, setBusy] = useState<string | null>(null);

  const load = useCallback(async () => {
    if (!on) return;
    try {
      setUpcoming(await store.upcomingReminders(workshop.id, REMINDER_HORIZON_DAYS));
      setSent(await store.sentReminders(workshop.id));
    } catch (e) {
      setError(e);
    }
  }, [on, store, workshop.id]);
  useEffect(() => void load(), [load]);

  if (!on) {
    return (
      <section>
        <h1>{t("reminders.title")}</h1>
        <p className="muted">{t("error.FEATURE_NOT_IN_PLAN.message")}</p>
      </section>
    );
  }

  const sendNow = async (vehicleId: string, kind: ReminderKind) => {
    setError(null);
    setInfo(null);
    setBusy(`${vehicleId}-${kind}`);
    try {
      const r = await store.sendReminderNow(vehicleId, kind);
      setInfo(t("reminders.sentNow", { to: r.sent.map((s) => s.to).join(", ") }));
    } catch (e) {
      setError(e);
    } finally {
      setBusy(null);
    }
    await load();
  };
  const today = todayIso();

  return (
    <section className="reminders-page">
      <h1>
        {t("reminders.title")} <Help id="reminders.page" />
      </h1>
      <p className="muted">{t("reminders.intro", { days: String(REMINDER_HORIZON_DAYS) })}</p>
      <ErrorText error={error} />
      {info && <p className="ok" role="status">{info}</p>}

      <h2>{t("reminders.upcoming")}</h2>
      {upcoming === null ? (
        <p>{t("common.loading")}</p>
      ) : upcoming.length === 0 ? (
        <p className="muted">{t("reminders.none")}</p>
      ) : (
        <ul className="reminder-list">
          {upcoming.map((r) => {
            const why = blocked(r);
            const id = `${r.vehicleId}-${r.kind}`;
            return (
              <li key={id} className={`reminder-card kind-${r.kind}`}>
                <div className="reminder-head">
                  <a
                    href={`#vehicles/${r.vehicleId}`}
                    onClick={(e) => {
                      e.preventDefault();
                      go("vehicles", r.vehicleId);
                    }}
                  >
                    <Plate plate={r.plateNumber} country={r.plateCountry} size="sm" />
                  </a>
                  <span className="reminder-kind">{t(`reminders.kind.${r.kind}`)}</span>
                </div>
                <p className="reminder-due">
                  <strong>{day(r.dueDate)}</strong>
                  {r.dueKm ? ` · ${r.dueKm.toLocaleString(i18n.language)} km` : ""}
                  {r.vehicleLabel ? <span className="muted"> · {r.vehicleLabel}</span> : null}
                </p>
                <p className="muted">
                  {r.customerName ?? t("vehicle.noOwner")}
                  {r.sentChannels.length > 0 && ` · ${t("reminders.sent")}: ${r.sentChannels.map((c) => t(`reminders.channel.${c}`)).join(", ")}`}
                  {!why && ` · ${r.sendFrom <= today ? t("reminders.goesToday") : t("reminders.goesOut", { date: day(r.sendFrom) })}`}
                </p>
                <div className="actions">
                  <button type="button" className="primary" disabled={!!why || busy === id} onClick={() => void sendNow(r.vehicleId, r.kind)}>
                    {t("reminders.sendNow")}
                    {!why && ` (${r.channels.map((c) => t(`reminders.channel.${c}`)).join(", ")})`}
                  </button>
                  {why && <span className={why === "reminders.why.allSent" ? "ok" : "missing"}>{t(why)}</span>}
                </div>
              </li>
            );
          })}
        </ul>
      )}

      <h2>{t("reminders.sent")}</h2>
      {sent.length === 0 ? (
        <p className="muted">{t("reminders.noneSent")}</p>
      ) : (
        <div className="table-scroll">
          <table className="list">
            <thead>
              <tr>
                <th>{t("reminders.col.sentAt")}</th>
                <th>{t("reminders.col.vehicle")}</th>
                <th>{t("reminders.col.what")}</th>
                <th>{t("reminders.col.due")}</th>
                <th>{t("reminders.col.channel")}</th>
                <th>{t("reminders.col.to")}</th>
              </tr>
            </thead>
            <tbody>
              {sent.map((s) => (
                <tr key={s.id} onClick={() => go("vehicles", s.vehicleId)}>
                  <td>{day(s.sentAt)}{s.manual && <span className="muted"> ({t("reminders.manual")})</span>}</td>
                  <td data-label={t("reminders.col.vehicle")}><Plate plate={s.plateNumber} country={s.plateCountry} size="sm" /></td>
                  <td data-label={t("reminders.col.what")}>{t(`reminders.kind.${s.kind}`)}</td>
                  <td data-label={t("reminders.col.due")}>{day(s.dueDate)}</td>
                  <td data-label={t("reminders.col.channel")}>{t(`reminders.channel.${s.channel}`)}</td>
                  <td data-label={t("reminders.col.to")}>{s.recipient}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </section>
  );
}
